/**
 * Maps a yar's quote/run result onto the smart assistant's answer message, so a
 * module yar shows the same answer card (text → KPI row → chart → result table →
 * source → footer) as «دستیار هوشمند». Display only: amounts stay the server's
 * strings, the footer's toman figure is the server's settled charge.
 */
import type {
  AiChart,
  AiDomain,
  AiEvidence,
  AiKpi,
  AiTurn,
} from '../../routes/ai/connect/contract.ts';
import type {
  AnswerMessage,
  BarChartSpec,
  KpiItem,
  ScopeKey,
  TableBlock,
  TableColumn,
} from '../../routes/ai/chat/types.ts';
import { faDigits } from '../../lib/faDigits.ts';
import { formatStockOnHand } from './stockDisplay.ts';

/** The evidence list is capped at this many rows by the server tools. */
export const EVIDENCE_CAP = 50;

const DOMAIN_LABEL: Readonly<Record<AiDomain, string>> = {
  inventory: 'انبار',
  sales: 'فروش',
  procurement: 'تدارکات',
  finance: 'حسابداری',
  projects: 'پروژه',
  sakhtar: 'سازمان',
};

/** The module the analyzing card names while it reads (finance reads the accounting ledger). */
export function scopeOf(domain: AiDomain): ScopeKey {
  switch (domain) {
    case 'finance':
      return 'accounting';
    case 'sakhtar':
      return 'inventory';
    default:
      return domain;
  }
}

export function kpiItems(kpis: readonly AiKpi[]): KpiItem[] {
  return kpis.map((kpi, index) => ({
    id: kpi.id ?? `kpi-${index}`,
    label: kpi.label,
    value: `${faDigits(kpi.value)}${kpi.unit ? ` ${kpi.unit}` : ''}`,
    ...(kpi.hint ? { hint: kpi.hint } : {}),
  }));
}

export function barChart(chart: AiChart): BarChartSpec {
  const caption = chart.unit ? `${chart.title} (${chart.unit})` : chart.title;
  return {
    id: 'yar-chart',
    kind: 'bar',
    caption,
    ariaLabel: caption,
    data: chart.items.map((item) => ({ label: item.label, value: item.value })),
  };
}

const COLUMNS: Readonly<Record<AiDomain, readonly TableColumn[]>> = {
  sales: [
    { key: 'title', label: 'عنوان فرصت' },
    { key: 'stage', label: 'مرحله' },
    { key: 'amount', label: 'مبلغ', align: 'num' },
  ],
  procurement: [
    { key: 'title', label: 'شناسه سفارش' },
    { key: 'status', label: 'وضعیت' },
    { key: 'amount', label: 'مبلغ', align: 'num' },
  ],
  finance: [
    { key: 'title', label: 'شناسه' },
    { key: 'side', label: 'سمت' },
    { key: 'due', label: 'سررسید' },
    { key: 'amount', label: 'مانده باز', align: 'num' },
  ],
  projects: [
    { key: 'title', label: 'کار' },
    { key: 'project', label: 'پروژه' },
    { key: 'late', label: 'عقب‌افتادگی', align: 'num' },
  ],
  inventory: [
    { key: 'title', label: 'انبار' },
    { key: 'qty', label: 'مقدار', align: 'num' },
  ],
  sakhtar: [
    { key: 'title', label: 'قلم' },
    { key: 'value', label: 'مقدار', align: 'num' },
  ],
};

function title(item: AiEvidence): string {
  return item.label ? `${item.label} (${item.recordId})` : item.recordId;
}

function rial(value: string | undefined): string {
  return `${faDigits(value ?? '0')} ریال`;
}

function cells(domain: AiDomain, item: AiEvidence): Record<string, string> {
  switch (domain) {
    case 'sales':
      return { title: title(item), stage: item.stage ?? '—', amount: rial(item.amountRial) };
    case 'procurement':
      return { title: title(item), status: item.status ?? '—', amount: rial(item.amountRial) };
    case 'finance': {
      const receivable = item.side === 'receivable' || item.status === 'receivable';
      return {
        title: item.recordId,
        side: receivable ? 'مطالبات' : 'بدهی‌ها',
        due: item.dueDate ? faDigits(item.dueDate) : '—',
        amount: rial(item.openBalanceRial),
      };
    }
    case 'projects':
      return {
        title: title(item),
        project: item.projectRef ?? '—',
        late: `${faDigits(item.daysOverdue ?? 0)} روز`,
      };
    case 'inventory': {
      const unit = (item as any).unit || 'عدد';
      return { title: title(item), qty: `${faDigits(formatStockOnHand(item.onHand ?? '0'))} ${unit}` };
    }
    case 'sakhtar':
      return {
        title: title(item),
        value:
          item.onHand !== undefined
            ? `${faDigits(formatStockOnHand(item.onHand))} عدد`
            : item.amountRial !== undefined
              ? rial(item.amountRial)
              : item.openBalanceRial !== undefined
                ? rial(item.openBalanceRial)
                : item.daysOverdue !== undefined
                  ? `${faDigits(item.daysOverdue)} روز`
                  : '—',
      };
  }
}

export function evidenceTable(domain: AiDomain, evidence: readonly AiEvidence[]): TableBlock {
  return {
    columns: COLUMNS[domain],
    rows: evidence.map((item, index) => ({
      id: `${item.recordId}-${index}`,
      cells: cells(domain, item),
    })),
  };
}

function answerMessage(
  id: string,
  domain: AiDomain,
  text: string,
  tokens: number,
  extra: Partial<AnswerMessage['content']>,
  basis: string | undefined,
): AnswerMessage {
  const label = DOMAIN_LABEL[domain];
  return {
    id,
    role: 'assistant',
    kind: 'answer',
    sampleId: 'server',
    content: { text: [text], ...extra },
    footer: { tokens, unit: 'توکن', sample: false, ...(basis ? { basis } : {}) },
    context: {
      path: [{ id: domain, label, kind: 'topic', messageId: id }],
      filters: [label],
      sources: [label],
    },
    isSample: false,
  };
}

export function isCasualGreeting(question?: string | null): boolean {
  if (!question) return false;
  const clean = question.trim().toLowerCase().replace(/[؟?!.,،]/g, '');
  if (!clean) return true;
  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length > 5) return false;
  const casualWords = [
    'سلام', 'درود', 'صبح بخیر', 'عصر بخیر', 'شب بخیر', 'وقت بخیر',
    'خوبی', 'چطوری', 'چه خبر', 'دیگه چه خبر', 'چخبر', 'چه خبرها',
    'کی هستی', 'تو کی هستی', 'معرفی', 'چیکار میکنی', 'چکار میکنی',
    'خسته نباشی', 'خسته نباشید', 'ممنون', 'مرسی', 'متشکر', 'تشکر', 'دمت گرم',
  ];
  return casualWords.some((w) => clean === w || clean.startsWith(w + ' ') || clean.endsWith(' ' + w));
}

/** A settled run: the server's answer, figures, rows and charge. */
export function yarAnswer(
  id: string,
  domain: AiDomain,
  turn: AiTurn,
  question?: string,
): AnswerMessage {
  const label = DOMAIN_LABEL[domain];
  const truncated =
    (turn as any).truncated === true ||
    (turn.evidence.length >= EVIDENCE_CAP && (turn as any).truncated !== false);
  const inTokens =
    turn.usage?.inputTokens !== null && turn.usage?.inputTokens !== undefined
      ? BigInt(turn.usage.inputTokens)
      : 0n;
  const outTokens =
    turn.usage?.outputTokens !== null && turn.usage?.outputTokens !== undefined
      ? BigInt(turn.usage.outputTokens)
      : 0n;
  const totalTokens =
    turn.charge == null
      ? 0
      : inTokens + outTokens > 0n
        ? Number(inTokens + outTokens)
        : turn.charge.toman != null
          ? Number(turn.charge.toman) * 5
          : 0;
  interface EvidenceItem {
    group?: string;
    source?: string;
    recordId?: string;
  }
  const docCitations = (turn.evidence as readonly EvidenceItem[]).filter(
    (e) => e.group === 'اسناد و آیین‌نامه‌ها' || e.source === 'doc-rag',
  );
  const docTitles = Array.from(new Set(docCitations.map((d) => d.recordId).filter(Boolean)));

  const effectiveQuestion = question ?? turn.question ?? undefined;
  const casual = isCasualGreeting(effectiveQuestion);
  const showVisuals = !casual;

  return answerMessage(
    id,
    domain,
    turn.answer ?? turn.stateLabel,
    totalTokens,
    {
      ...(showVisuals && turn.kpis && turn.kpis.length > 0 ? { kpis: kpiItems(turn.kpis) } : {}),
      ...(showVisuals && turn.chart && turn.chart.items.length > 0 ? { chart: barChart(turn.chart) } : {}),
      ...(showVisuals && turn.evidence.length > 0 ? { table: evidenceTable(domain, turn.evidence) } : {}),
      ...(showVisuals && turn.evidence.length > 0
        ? {
            source: {
              lines: [
                `داده‌های ثبت‌شدهٔ ${label}: ${faDigits(turn.evidence.length)} ردیف`,
                ...(docTitles.length > 0 ? [`مستندات استنادشده: ${docTitles.join('، ')}`] : []),
                ...(truncated
                  ? [
                      `فهرست در سقف ${faDigits(EVIDENCE_CAP)} قلم قطع شده است (فهرست ناقص، جمع کامل).`,
                    ]
                  : []),
              ],
              postedOnly: false,
            },
          }
        : {}),
    },
    `بر اساس داده‌های ثبت‌شدهٔ ${label}`,
  );
}

/** No run happened (e.g. «داده کافی موجود نیست»): a notice answer, nothing charged. */
export function yarNotice(id: string, domain: AiDomain, text: string): AnswerMessage {
  return answerMessage(id, domain, text, 0, {}, undefined);
}
